<template>
    <!--
        Read mode reuses the read-grid classes (EntityDetailsPropertiesTab__readGrid/__readLabel/
        __readValue/__readEmpty) defined in the parent EntityDetailsPropertiesTab.vue's global
        (non-scoped) style block. This card is only ever rendered inside that tab; if it is reused
        elsewhere, or the parent style is made `scoped`, those classes must be localized here.
    -->
    <EntityDetailsPropertiesTabCard
        class="EntityDetailsProducersCard"
        title="Producers"
        :editing="isEditing"
    >
        <template v-if="isEditing && !isDisabledByRbac" #titleRight>
            <!--
                The tooltip hangs off the wrapper rather than the button: a disabled NewButton sets
                `pointer-events: none`, so the button itself never sees the hover that opens it.
            -->
            <span v-tooltip="addTooltip">
                <NewButton
                    variant="flat"
                    color="primary"
                    size="sm"
                    :disabled="locked"
                    data-test-id="EntityDetailsProducersCard__addButton"
                    class="EntityDetailsProducersCard__addButton"
                    @click="onAddProducer"
                >
                    <EidIcon
                        :icon-src="faPlus"
                        alt="add"
                        class="EntityDetailsProducersCard__addButtonIcon"
                    />
                    Add Producer
                </NewButton>
            </span>
        </template>

        <div v-if="isEditing" class="EntityDetailsProducersCard__editList">
            <!--
                Withheld from a user who may not edit producers at all: their controls are gone
                because of their permissions, and assigning requirements would not bring them back.
            -->
            <p
                v-if="locked && !isDisabledByRbac"
                class="EntityDetailsProducersCard__lockNotice"
                data-test-id="EntityDetailsProducersCard__lockNotice"
            >
                <EidIcon
                    :icon-src="faInfoCircle"
                    alt="note"
                    class="EntityDetailsProducersCard__lockNoticeIcon"
                />
                <span>{{ lockMessage }}</span>
                <NewButton
                    v-if="waitingForWcJurisdictions && wcJurisdictionsFailed"
                    variant="flat"
                    color="primary"
                    size="sm"
                    data-test-id="EntityDetailsProducersCard__retryWcJurisdictions"
                    @click="$emit('retry-wc-jurisdictions')"
                >
                    Try again
                </NewButton>
            </p>

            <div
                class="EntityDetailsProducersCard__lockable"
                :class="{ 'EntityDetailsProducersCard__lockable--locked': locked }"
                data-test-id="EntityDetailsProducersCard__lockable"
            >
                <EntityDetailsProducerEditRow
                    v-for="row in rows"
                    :key="row.key"
                    :row="row"
                    :coverage-options="coverageOptions"
                    :errors="rowErrors[row.key] || {}"
                    :show-errors="saveTouched"
                    :disabled="editingDisabled"
                    :show-remove="!isDisabledByRbac"
                    :coverage-required="assignableCoverageTypes.length > 0"
                    :selectable="rows.length > 1"
                    :default-key="defaultRowKey"
                    @field-change="onFieldChange"
                    @phone-change="onPhoneChange"
                    @coverages-change="onCoveragesChange"
                    @set-default="onSetDefault"
                    @remove="onRemoveProducer"
                />

                <!-- Withheld while locked: it invites an Add the lock notice above has just ruled out. -->
                <p
                    v-if="rows.length === 0 && !locked"
                    class="EntityDetailsProducersCard__empty"
                    data-test-id="EntityDetailsProducersCard__editEmpty"
                >
                    No producers. Add one, or save to leave this entity without a producer.
                </p>
            </div>

            <p
                v-if="saveTouched && missingDefaultError"
                class="EntityDetailsProducersCard__cardError"
                data-test-id="EntityDetailsProducersCard__missingDefaultError"
            >
                {{ missingDefaultError }}
            </p>

            <p
                v-if="saveTouched && unassignedCoverageError"
                class="EntityDetailsProducersCard__cardError"
                data-test-id="EntityDetailsProducersCard__unassignedCoverageError"
            >
                {{ unassignedCoverageError }}
            </p>
        </div>

        <div v-else class="EntityDetailsProducersCard__list">
            <div
                v-for="(producer, index) in producers"
                :key="index"
                class="EntityDetailsProducersCard__producer"
                :class="{ 'EntityDetailsProducersCard__producer--default': producer.isDefault }"
            >
                <div class="EntityDetailsProducersCard__firmRow">
                    <h4
                        class="EntityDetailsProducersCard__firm"
                        data-test-id="EntityDetailsProducersCard__firm"
                    >
                        {{ producer.producerFirm || '—' }}
                    </h4>
                    <LabelBadge
                        v-if="producer.isDefault"
                        variant="gradient"
                        size="xs"
                        data-test-id="EntityDetailsProducersCard__defaultBadge"
                    >
                        DEFAULT
                    </LabelBadge>
                </div>
                <p
                    v-if="producer.isDefault"
                    class="EntityDetailsProducersCard__defaultNote"
                >
                    Receives new requirement notifications
                </p>

                <div class="EntityDetailsPropertiesTab__readGrid">
                    <span class="EntityDetailsPropertiesTab__readLabel">Contact Name</span>
                    <span
                        class="EntityDetailsPropertiesTab__readValue"
                        :class="{ 'EntityDetailsPropertiesTab__readEmpty': !producer.contactName }"
                        data-test-id="EntityDetailsProducersCard__contactName"
                    >{{ producer.contactName || '—' }}</span>

                    <span class="EntityDetailsPropertiesTab__readLabel">Email</span>
                    <span
                        class="EntityDetailsPropertiesTab__readValue"
                        :class="{ 'EntityDetailsPropertiesTab__readEmpty': !producer.email }"
                        data-test-id="EntityDetailsProducersCard__email"
                    >{{ producer.email || '—' }}</span>

                    <span class="EntityDetailsPropertiesTab__readLabel">Phone</span>
                    <span
                        class="EntityDetailsPropertiesTab__readValue"
                        :class="{ 'EntityDetailsPropertiesTab__readEmpty': !producer.phone }"
                        data-test-id="EntityDetailsProducersCard__phone"
                    >{{ producer.phone || '—' }}</span>

                    <span class="EntityDetailsPropertiesTab__readLabel">Status</span>
                    <span
                        class="EntityDetailsPropertiesTab__readValue"
                        data-test-id="EntityDetailsProducersCard__status"
                    >{{ producer.isActive ? 'Active' : 'Inactive' }}</span>

                    <span class="EntityDetailsPropertiesTab__readLabel">Coverage Types</span>
                    <span
                        class="EntityDetailsPropertiesTab__readValue"
                        :class="{ 'EntityDetailsPropertiesTab__readEmpty': (producer.coverageTypes || []).length === 0 }"
                        data-test-id="EntityDetailsProducersCard__coverageTypes"
                    >{{ formatCoverageTypes(producer.coverageTypes) }}</span>
                </div>
            </div>

            <p
                v-if="producers.length === 0"
                class="EntityDetailsProducersCard__empty"
                data-test-id="EntityDetailsProducersCard__empty"
            >
                No producers associated with this entity
            </p>
        </div>
    </EntityDetailsPropertiesTabCard>
</template>

<style lang="scss">
    .EntityDetailsProducersCard {
        &__list {
            display: flex;
            flex-direction: column;
            padding: 16px 0;
        }

        &__editList {
            display: flex;
            flex-direction: column;
            padding: 4px 0 18px;
        }

        &__lockable {
            display: flex;
            flex-direction: column;

            // Reads as an overlay over the whole list without covering it: the controls inside are
            // already disabled, and a real overlay would swallow the tooltips that explain why.
            &--locked {
                opacity: 0.55;
            }
        }

        &__lockNotice {
            display: flex;
            align-items: center;
            gap: 7px;
            margin: 6px 0 0;
            font-family: "Roboto", sans-serif;
            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
            color: var(--eidQualityBlack-75);
        }

        &__lockNoticeIcon {
            color: var(--eidQualityBlack-50);
        }

        &__producer {
            & + & {
                margin-top: 16px;
                padding-top: 16px;
                border-top: 1px solid var(--eidSecureBlue-10);
            }

            // Readable at a glance down a column of otherwise identical entries.
            &--default {
                border-left: 3px solid var(--eidTrustGreen);
                padding-left: 13px;
            }
        }

        &__firmRow {
            display: flex;
            align-items: center;
            gap: 10px;
            margin-bottom: 10px;
        }

        &__defaultNote {
            margin: -4px 0 10px;
            font-family: "Roboto", sans-serif;
            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
            color: var(--eidQualityBlack-75);
        }

        &__firm {
            margin: 0;
            font-family: "Roboto", sans-serif;
            font-size: 14px;
            font-weight: 500;
            line-height: 20px;
            color: var(--eidSecureBlue);
        }

        &__addButton {
            display: flex;
            align-items: center;
            gap: 6px;
            font-family: "Roboto", sans-serif;
            font-size: 12px;
            font-weight: 500;
        }

        &__addButtonIcon {
            font-size: 11px;
        }

        &__empty {
            margin: 0;
            font-family: "Roboto", sans-serif;
            font-size: 13px;
            font-weight: 400;
            line-height: 20px;
            color: var(--eidQualityBlack-50);
            text-align: center;
            padding: 16px 0;
        }

        &__cardError {
            margin: 14px 0 0;
            color: var(--eidDangerRed);
        }
    }
</style>

<script lang="ts">
    import { defineComponent, PropType } from 'vue';

    import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
    import { faPlus } from '@fortawesome/pro-light-svg-icons';

    import { DropdownOption } from '@evidentid/dashboard-commons/components/Dropdown/types';
    import EidIcon from '@evidentid/dashboard-commons/components/EidIcon/EidIcon.vue';
    import LabelBadge from '@evidentid/dashboard-commons/components/LabelBadge/LabelBadge.vue';
    import { GovernmentIssuedWcJurisdictions } from '@evidentid/tprm-portal-lib/models/common';
    import { EntityAddress, Producer } from '@evidentid/tprm-portal-lib/models/dashboard';

    import NewButton from '@/components/new-button/NewButton.vue';
    import { CategorizedEnumLabels } from '@/modules/dashboard/models/CategorizedEnumLabels.model';
    import { EnumCategories } from '@/modules/dashboard/models/EnumCategories.model';
    import {
        ProducerRow,
        ProducerRowErrors,
        ProducerRowField,
        assignCoverageTypes,
        createProducerRow,
        hasDefaultProducer,
        setDefaultProducer,
        MISSING_DEFAULT_MESSAGE,
        getAssignableCoverageTypes,
        getUnassignedCoverageTypes,
        haveProducersChanged,
        unassignedCoverageMessage,
        validateProducerRows,
        WORKERS_COMPENSATION_TYPE,
    } from '@/modules/entity-details/utils/producer-forms/producerRows';

    import EntityDetailsProducerEditRow from './EntityDetailsProducerEditRow.vue';
    import EntityDetailsPropertiesTabCard from './EntityDetailsPropertiesTabCard.vue';

    /** Shown above the producer list while the entity has no requirements to assign. */
    const MISSING_COVERAGE_MESSAGE =
        'Producers cannot be edited until this entity has requirements assigned.';

    /** The same reason, aimed at the disabled Add Producer button. */
    const MISSING_COVERAGE_ADD_MESSAGE =
        'You cannot add a producer until this entity has requirements assigned.';

    /** Shown while the government-issued WC jurisdictions are still loading. */
    const WC_JURISDICTIONS_LOADING_MESSAGE =
        'Producers can be edited once the workers compensation rules have loaded.';

    /** Shown once loading the government-issued WC jurisdictions has given up. */
    const WC_JURISDICTIONS_FAILED_MESSAGE =
        'Producers cannot be edited because the workers compensation rules did not load.';

    export default defineComponent({
        name: 'EntityDetailsProducersCard',
        components: {
            EidIcon,
            LabelBadge,
            NewButton,
            EntityDetailsPropertiesTabCard,
            EntityDetailsProducerEditRow,
        },
        props: {
            producers: {
                type: Array as PropType<Producer[]>,
                default: (): Producer[] => [],
            },
            enumLabels: {
                type: Object as PropType<CategorizedEnumLabels>,
                default: (): CategorizedEnumLabels => ({}) as CategorizedEnumLabels,
            },
            isEditing: {
                type: Boolean,
                default: false,
            },
            isDisabledByRbac: {
                type: Boolean,
                default: false,
            },
            // Errors stay hidden until a save is attempted, matching the rest of the tab.
            saveTouched: {
                type: Boolean,
                default: false,
            },
            rows: {
                type: Array as PropType<ProducerRow[]>,
                default: () => [],
            },
            // The coverage types this entity has requirements for — every one of them must end up
            // assigned to some producer.
            coverageTypes: {
                type: Array as PropType<string[]>,
                default: () => [],
            },
            // The entity's address as the form currently holds it. Decides whether workers
            // compensation is the entity's own responsibility and so withheld from producers.
            entityAddress: {
                type: Object as PropType<Partial<EntityAddress> | null>,
                default: null,
            },
            // eid-insurance's government-issued WC jurisdictions, checked against `entityAddress`.
            // `null` means not loaded yet, which is not the same as "not government-issued".
            wcJurisdictions: {
                type: Object as PropType<GovernmentIssuedWcJurisdictions | null>,
                default: null,
            },
            // Whether the jurisdictions load gave up. `wcJurisdictions` is null either way; this only
            // changes what the card says and offers a retry.
            wcJurisdictionsFailed: {
                type: Boolean,
                default: false,
            },
            // Whether `coverageTypes` reflects a completed load. A failed fetch also arrives as an
            // empty list, and locking on that would blame the entity for a network error, so the
            // card stays unlocked until the requirements are known.
            requirementsLoaded: {
                type: Boolean,
                default: true,
            },
            // A producer may not reuse the entity's own contact email.
            entityContactEmail: {
                type: String,
                default: '',
            },
            // Per-row field errors the API refused the last save on, keyed by row key.
            serverErrors: {
                type: Object as PropType<Record<string, ProducerRowErrors>>,
                default: () => ({}),
            },
        },
        emits: [ 'rows-change', 'validity-change', 'retry-wc-jurisdictions' ],
        data() {
            return { faPlus, faInfoCircle };
        },
        computed: {
            /** The entity's coverage types the dropdown actually offers, ordered by label.
             *
             * Kept as a named computed so the options, the required flag, the per-row validation
             * and the "everything is assigned" rule all read from one list — which also keeps the
             * dropdown and the unassigned-coverage message listing the same types in the same
             * order.
             *
             * Ordered on the label rather than the key: the label is what the reader scans, and the
             * two disagree often enough that key order looks arbitrary. Copied before sorting,
             * since the underlying array can be the `coverageTypes` prop itself.
             */
            assignableCoverageTypes(): string[] {
                const assignable = getAssignableCoverageTypes(
                    this.coverageTypes, this.entityAddress, this.wcJurisdictions,
                );
                return [ ...assignable ].sort(
                    (a, b) => this.getCoverageTypeLabel(a).localeCompare(
                        this.getCoverageTypeLabel(b), undefined, { sensitivity: 'base' },
                    ),
                );
            },
            /** Whether producer editing is off because the entity has no requirements at all.
             *
             * A producer is only useful once it is given a coverage type, and an entity with no
             * risk profile has none to give, so setting one up cannot be completed.
             *
             * The test is on the entity having requirements rather than on
             * `assignableCoverageTypes`: an entity whose only requirement is workers compensation,
             * in a jurisdiction where that is its own responsibility, leaves a producer nothing it
             * may hold — but that is a settled shape rather than a gap waiting to be filled, and a
             * lock on it would never lift. Coverage is optional there instead, via
             * `validateProducerRows`.
             *
             * Unlike `isDisabledByRbac`, which withholds the controls outright, this is a passing
             * state of the entity's own data: the fields stay on screen, disabled and explained,
             * so it is clear they come back once requirements are assigned.
             */
            lockedByMissingCoverage(): boolean {
                return this.requirementsLoaded && this.coverageTypes.length === 0;
            },
            /** Whether producer editing waits on the government-issued WC jurisdictions.
             *
             * Without them the card cannot tell whether workers compensation belongs to the entity
             * or to a producer. Guessing "producer" would demand it be assigned to one, and a save
             * made on that guess keeps routing it to that producer, so nothing but removal is
             * editable until the list is known. Removal stays open, as it does under the other
             * lock: it is the only way to clean up a producer, and it says nothing about who holds
             * workers compensation. An entity with no workers compensation requirement is
             * unaffected.
             */
            waitingForWcJurisdictions(): boolean {
                return this.wcJurisdictions === null
                    && this.coverageTypes.includes(WORKERS_COMPENSATION_TYPE);
            },
            locked(): boolean {
                return this.lockedByMissingCoverage || this.waitingForWcJurisdictions;
            },
            editingDisabled(): boolean {
                return this.isDisabledByRbac || this.locked;
            },
            lockMessage(): string {
                if (this.lockedByMissingCoverage) {
                    return MISSING_COVERAGE_MESSAGE;
                }
                return this.wcJurisdictionsFailed
                    ? WC_JURISDICTIONS_FAILED_MESSAGE
                    : WC_JURISDICTIONS_LOADING_MESSAGE;
            },
            addTooltip(): string | Record<string, unknown> {
                if (!this.locked) {
                    return '';
                }
                return {
                    popperClass: 'VTooltip--dark',
                    content: this.lockedByMissingCoverage ? MISSING_COVERAGE_ADD_MESSAGE : this.lockMessage,
                };
            },
            coverageOptions(): DropdownOption[] {
                return this.assignableCoverageTypes.map((coverageType) => ({
                    value: coverageType,
                    label: this.getCoverageTypeLabel(coverageType),
                }));
            },
            /** Whether the user has actually touched the producer list.
             *
             * Producer rules are only enforced once they have. An entity can arrive with producer
             * data that already fails them — a producer with no coverage type assigned, say, or a
             * coverage type nobody handles — and blocking an unrelated edit to the legal name
             * behind data the user never touched would be wrong. Editing the list at all opts into
             * the rules for the whole list, since the save replaces it outright.
             */
            hasProducerEdits(): boolean {
                return haveProducersChanged(this.rows, this.producers);
            },
            clientErrors(): Record<string, ProducerRowErrors> {
                // Nothing is enforced while the card is locked, by either lock. Removal is the only
                // edit left open there, and holding a required field against a row whose inputs are disabled
                // would leave the removal unsavable.
                return this.hasProducerEdits && !this.locked
                    ? validateProducerRows(this.rows, this.entityContactEmail, this.assignableCoverageTypes)
                    : {};
            },
            rowErrors(): Record<string, ProducerRowErrors> {
                // Client rules win over a stale server message for the same field: the user has
                // had a chance to edit since the refusal, and the client check reflects what is
                // on screen right now.
                // Only the client rules are gated on the list having been edited. A server error
                // describes the payload the API just refused, which always contains the whole
                // producer list — dropping it here would leave the refusal unreported, since the
                // page-level alert stands down precisely because these exist.
                const merged: Record<string, ProducerRowErrors> = {};
                this.rows.forEach((row) => {
                    const combined = { ...this.serverErrors[row.key], ...this.clientErrors[row.key] };
                    if (Object.keys(combined).length > 0) {
                        merged[row.key] = combined;
                    }
                });
                return merged;
            },
            unassignedCoverageTypes(): string[] {
                // Removing every producer is a supported edit — the entity falls back to being
                // asked for its own documents — so an empty list is not "coverage left
                // unassigned". Applying the rule to it would make that edit unsavable.
                if (!this.hasProducerEdits || this.rows.length === 0 || this.locked) {
                    return [];
                }
                return getUnassignedCoverageTypes(this.rows, this.assignableCoverageTypes);
            },
            defaultRowKey(): string {
                return this.rows.find((row) => row.isDefault)?.key || '';
            },
            /** Surfaced only once the list has been edited and still has rows: an entity whose
             * producers were all removed has nothing to designate, and one whose stored list
             * predates the designation is given a fallback by toProducerRows rather than an
             * error. A locked card disables the radios, so it never asks for a choice. */
            missingDefaultError(): string {
                return this.hasProducerEdits
                    && !this.locked
                    && this.rows.length > 0
                    && !hasDefaultProducer(this.rows)
                    ? MISSING_DEFAULT_MESSAGE
                    : '';
            },
            unassignedCoverageError(): string {
                return this.unassignedCoverageTypes.length > 0
                    ? unassignedCoverageMessage(
                        this.unassignedCoverageTypes.map((type) => this.getCoverageTypeLabel(type)),
                    )
                    : '';
            },
            isValid(): boolean {
                return Object.keys(this.rowErrors).length === 0
                    && !this.unassignedCoverageError
                    && !this.missingDefaultError;
            },
        },
        watch: {
            isValid: {
                immediate: true,
                handler(valid: boolean): void {
                    this.$emit('validity-change', valid);
                },
            },
        },
        methods: {
            getCoverageTypeLabel(coverageType: string): string {
                const labels = this.enumLabels[EnumCategories.coverageTypes] || {};
                return labels[coverageType]?.label || coverageType;
            },
            formatCoverageTypes(coverageTypes: string[] = []): string {
                return coverageTypes.length > 0
                    ? coverageTypes.map((coverageType) => this.getCoverageTypeLabel(coverageType)).join(', ')
                    : '—';
            },
            emitRows(rows: ProducerRow[]): void {
                this.$emit('rows-change', rows);
            },
            onAddProducer(): void {
                const row = createProducerRow();
                // The only producer is always the default: below two rows the edit row offers no
                // radio, so a lone non-default row could never be designated or saved.
                this.emitRows(this.rows.length === 0
                    ? [ { ...row, isDefault: true } ]
                    : [ ...this.rows, row ]);
            },
            onRemoveProducer(key: string): void {
                // The removed row's designation is not passed on: with other producers left, the
                // replacement is the user's choice, and missingDefaultError blocks the save until
                // they make it. A lone survivor is nobody's choice to make — and below two rows
                // the edit row offers no radio — so it inherits the designation.
                const remaining = this.rows.filter((row) => row.key !== key);
                this.emitRows(remaining.length === 1 && !remaining[0].isDefault
                    ? [ { ...remaining[0], isDefault: true } ]
                    : remaining);
            },
            onSetDefault(key: string): void {
                this.emitRows(setDefaultProducer(this.rows, key));
            },
            onFieldChange(key: string, field: ProducerRowField, value: string): void {
                this.emitRows(this.rows.map(
                    (row) => (row.key === key ? { ...row, [field]: value } : row),
                ));
            },
            onPhoneChange(key: string, phone: string, phoneValid: boolean): void {
                this.emitRows(this.rows.map(
                    (row) => (row.key === key ? { ...row, phone, phoneValid } : row),
                ));
            },
            onCoveragesChange(key: string, coverageTypes: string[]): void {
                this.emitRows(
                    assignCoverageTypes(this.rows, key, coverageTypes),
                );
            },
        },
    });
</script>
